import { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Search, X, Plus, Clock, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Exercise, MuscleGroup } from '@/types';
import { MUSCLE_GROUPS } from '@/types';

interface ExercisePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (exercise: Exercise) => void;
  onCreateExercise: (name: string, muscleGroup?: MuscleGroup) => void;
  exercises: Exercise[];
  recentExerciseIds?: string[];
  isCreating?: boolean;
}

export function ExercisePickerModal({
  isOpen,
  onClose,
  onSelect,
  onCreateExercise,
  exercises,
  recentExerciseIds = [],
  isCreating = false,
}: ExercisePickerModalProps) {
  const [search, setSearch] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<MuscleGroup | 'all'>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseMuscleGroup, setNewExerciseMuscleGroup] = useState<MuscleGroup | ''>('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedMuscleGroup('all');
      setShowCreateForm(false);
      setNewExerciseName('');
      setNewExerciseMuscleGroup('');
      // Focus search input after modal animation
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Focus create input when switching to create form
  useEffect(() => {
    if (showCreateForm) {
      setTimeout(() => {
        createInputRef.current?.focus();
      }, 50);
    }
  }, [showCreateForm]);

  const handleClose = () => {
    setSearch('');
    setShowCreateForm(false);
    setNewExerciseName('');
    setNewExerciseMuscleGroup('');
    onClose();
  };

  const handleSelect = (exercise: Exercise) => {
    onSelect(exercise);
    handleClose();
  };

  const handleCreateExercise = () => {
    if (newExerciseName.trim()) {
      onCreateExercise(
        newExerciseName.trim(),
        newExerciseMuscleGroup || undefined
      );
      // Don't close - parent will close on success
    }
  };

  const handleStartCreate = () => {
    setShowCreateForm(true);
    setNewExerciseName(search);
  };

  // Filter exercises based on search and muscle group
  const filteredExercises = exercises.filter((e) => {
    // Filter by search
    if (search.trim() && !e.name.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    // Filter by muscle group
    if (selectedMuscleGroup !== 'all' && e.muscleGroup !== selectedMuscleGroup) {
      return false;
    }
    return true;
  });

  // Get recent exercises
  const recentExercises = recentExerciseIds
    .map((id) => exercises.find((e) => e.id === id))
    .filter((e): e is Exercise => e !== undefined)
    .slice(0, 5);

  // Get muscle group label
  const getMuscleGroupLabel = (muscleGroup?: string | null) => {
    if (!muscleGroup) return null;
    const group = MUSCLE_GROUPS.find((g) => g.id === muscleGroup);
    return group ? `${group.emoji} ${group.name}` : muscleGroup;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      {showCreateForm ? 'Luo uusi liike' : 'Valitse liike'}
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={handleClose}
                      className={cn(
                        'p-2 rounded-lg text-gray-400',
                        'hover:text-gray-600 hover:bg-gray-100',
                        'dark:hover:text-gray-300 dark:hover:bg-gray-700',
                        'active:bg-gray-200 dark:active:bg-gray-600',
                        'transition-colors touch-manipulation'
                      )}
                      aria-label="Sulje"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  {showCreateForm ? (
                    /* Create exercise form */
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Liikkeen nimi *
                        </label>
                        <input
                          ref={createInputRef}
                          type="text"
                          value={newExerciseName}
                          onChange={(e) => setNewExerciseName(e.target.value)}
                          placeholder="esim. Bulgarian split squat"
                          className={cn(
                            'w-full min-h-[48px] px-4 text-base',
                            'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl',
                            'text-gray-900 dark:text-white placeholder-gray-400',
                            'focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Lihasryhmä (valinnainen)
                        </label>
                        <select
                          value={newExerciseMuscleGroup}
                          onChange={(e) => setNewExerciseMuscleGroup(e.target.value as MuscleGroup | '')}
                          className={cn(
                            'w-full min-h-[48px] px-4 text-base',
                            'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl',
                            'text-gray-900 dark:text-white',
                            'focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                          )}
                        >
                          <option value="">Valitse lihasryhmä...</option>
                          {MUSCLE_GROUPS.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.emoji} {group.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateForm(false);
                            setNewExerciseName('');
                            setNewExerciseMuscleGroup('');
                          }}
                          className={cn(
                            'flex-1 min-h-[48px] px-4 font-medium rounded-xl',
                            'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                            'transition-all duration-200 touch-manipulation',
                            'hover:bg-gray-200 dark:hover:bg-gray-600',
                            'active:scale-[0.98] active:bg-gray-300'
                          )}
                        >
                          Takaisin
                        </button>
                        <button
                          type="button"
                          onClick={handleCreateExercise}
                          disabled={!newExerciseName.trim() || isCreating}
                          className={cn(
                            'flex-1 flex items-center justify-center gap-2 min-h-[48px] px-4 font-medium rounded-xl',
                            'bg-primary-600 text-white',
                            'transition-all duration-200 touch-manipulation',
                            'hover:bg-primary-700',
                            'active:scale-[0.98] active:bg-primary-800',
                            'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100'
                          )}
                        >
                          {isCreating ? (
                            <span className="animate-pulse">Luodaan...</span>
                          ) : (
                            <>
                              <Plus className="h-4 w-4" />
                              Luo liike
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Search input */
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Hae liikettä..."
                        className={cn(
                          'w-full min-h-[48px] pl-12 pr-12 text-base',
                          'bg-gray-100 dark:bg-gray-900 border-0 rounded-xl',
                          'text-gray-900 dark:text-white placeholder-gray-400',
                          'focus:outline-hidden focus:ring-2 focus:ring-primary-500'
                        )}
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch('')}
                          className={cn(
                            'absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full',
                            'text-gray-400 hover:text-gray-600 hover:bg-gray-200',
                            'dark:hover:text-gray-300 dark:hover:bg-gray-700',
                            'transition-colors touch-manipulation'
                          )}
                          aria-label="Tyhjennä haku"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Muscle group filter chips - sticky below header */}
                {!showCreateForm && (
                  <div className="shrink-0 px-4 py-2 border-b border-gray-100 dark:border-gray-700 overflow-x-auto scrollbar-hide bg-white dark:bg-gray-800">
                    <div className="flex gap-2 min-w-max">
                      <button
                        type="button"
                        onClick={() => setSelectedMuscleGroup('all')}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap',
                          'transition-all touch-manipulation active:scale-95',
                          selectedMuscleGroup === 'all'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        )}
                      >
                        Kaikki
                      </button>
                      {MUSCLE_GROUPS.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => setSelectedMuscleGroup(group.id)}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap',
                            'transition-all touch-manipulation active:scale-95',
                            selectedMuscleGroup === group.id
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          )}
                        >
                          {group.emoji} {group.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content */}
                {!showCreateForm && (
                  <div className="flex-1 overflow-y-auto">
                    {/* Create new exercise button */}
                    <div className="p-2">
                      <button
                        type="button"
                        onClick={handleStartCreate}
                        className={cn(
                          'w-full flex items-center gap-3 p-4 rounded-xl',
                          'bg-primary-50 dark:bg-primary-900/20',
                          'border border-primary-200 dark:border-primary-800',
                          'text-left transition-all touch-manipulation',
                          'hover:bg-primary-100 dark:hover:bg-primary-900/30',
                          'active:scale-[0.99] active:bg-primary-200 dark:active:bg-primary-900/40'
                        )}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                          <Plus className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-primary-700 dark:text-primary-300">
                            {search ? `Luo "${search}"` : 'Luo uusi liike'}
                          </p>
                          <p className="text-sm text-primary-600 dark:text-primary-400">
                            Lisää oma liike, jos sopivaa ei löydy
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-primary-400" />
                      </button>
                    </div>

                    {/* Recent exercises (only when not searching) */}
                    {!search && recentExercises.length > 0 && (
                      <div className="px-2 pb-2">
                        <div className="flex items-center gap-2 px-2 py-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Viimeisimmät
                          </span>
                        </div>
                        <div className="space-y-1">
                          {recentExercises.map((exercise) => (
                            <ExerciseRow
                              key={exercise.id}
                              exercise={exercise}
                              muscleGroupLabel={getMuscleGroupLabel(exercise.muscleGroup)}
                              onSelect={() => handleSelect(exercise)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Divider */}
                    {!search && recentExercises.length > 0 && filteredExercises.length > 0 && (
                      <div className="px-4 py-2">
                        <div className="border-t border-gray-200 dark:border-gray-700" />
                      </div>
                    )}

                    {/* All exercises / Search results */}
                    <div className="px-2 pb-4">
                      {!search && (
                        <div className="px-2 py-2">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Kaikki liikkeet ({filteredExercises.length})
                          </span>
                        </div>
                      )}

                      {filteredExercises.length === 0 ? (
                        <div className="text-center py-10 px-4 animate-fade-in">
                          <div className="w-16 h-16 bg-linear-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                            <Search className="h-7 w-7 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                          </div>
                          <p className="text-gray-900 dark:text-white font-medium mb-1">
                            Ei tuloksia
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Hakusanalla "{search || selectedMuscleGroup}" ei löytynyt liikkeitä
                          </p>
                          <button
                            type="button"
                            onClick={handleStartCreate}
                            className={cn(
                              'inline-flex items-center justify-center gap-2',
                              'px-4 py-2.5 font-medium rounded-lg',
                              'bg-primary-600 text-white',
                              'transition-all duration-200 touch-manipulation',
                              'hover:bg-primary-700 active:scale-[0.98]',
                              'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
                            )}
                          >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                            Luo "{search || 'uusi liike'}"
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredExercises.map((exercise) => (
                            <ExerciseRow
                              key={exercise.id}
                              exercise={exercise}
                              muscleGroupLabel={getMuscleGroupLabel(exercise.muscleGroup)}
                              onSelect={() => handleSelect(exercise)}
                              highlight={search}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Individual exercise row component
function ExerciseRow({
  exercise,
  muscleGroupLabel,
  onSelect,
  highlight,
}: {
  exercise: Exercise;
  muscleGroupLabel: string | null;
  onSelect: () => void;
  highlight?: string;
}) {
  // Highlight matching text
  const highlightText = (text: string, query?: string) => {
    if (!query) return text;
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 dark:bg-yellow-900/50 rounded-sm px-0.5">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 min-h-[52px] px-3 py-2 rounded-xl',
        'text-left transition-all touch-manipulation',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        'active:scale-[0.99] active:bg-gray-200 dark:active:bg-gray-600',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
      )}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 dark:text-white truncate">
          {highlightText(exercise.name, highlight)}
        </p>
        {muscleGroupLabel && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {muscleGroupLabel}
          </p>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-gray-300 dark:text-gray-600 shrink-0" />
    </button>
  );
}
